import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import Tile from "@src/components/containers/Tile";
import UserAvatar from "@src/components/common/UserDisplay/UserAvatar";
import { UserAvatarSizes } from "@src/components/common/UserDisplay/UserAvatarSizes";
import AccountHiddenItemSettings from "@src/components/widgets/AccountHiddenItemSettings";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { Clinic } from "@src/types/clinic";
import {
  ClinicUserRoles,
  ClinicUserStatuses,
} from "@src/constants/ClinicUserConstants";
import * as TeamApi from "@src/api/TeamApi";
import { TeamMember } from "@src/api/TeamApi";
import ClinicSuppliersTable from "./ClinicSuppliersTable";
import EditClinicInfoModal from "./EditClinicInfoModal";
import styles from "./ManageClinicDetailPage.module.scss";

function userInitials(fullName: string | null | undefined): string {
  if (!fullName) return "";
  return fullName
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

function formatAddressLine(clinic: Clinic): string {
  const parts = [clinic.address1, clinic.city, clinic.state]
    .filter((p) => !!p && p.trim().length > 0)
    .join(", ");
  return [parts, clinic.zipCode].filter(Boolean).join(" ").trim();
}

function clinicInitials(clinic: Clinic): string {
  const words = clinic.name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w.charAt(0).toUpperCase()).join("");
}

export default function ManageClinicDetailPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { currentUser, setActiveClinic } = useCurrentUserStore();
  const [admins, setAdmins] = useState<TeamMember[]>([]);
  const [editOpen, setEditOpen] = useState(false);

  const clinicUser = useMemo(
    () =>
      currentUser?.clinicUsers?.find(
        (cu) => cu.clinic.id === clinicId,
      ) ?? null,
    [currentUser, clinicId],
  );

  const isViewingActiveClinic =
    !!currentUser?.activeClinic && currentUser.activeClinic.id === clinicId;

  useEffect(() => {
    if (clinicUser && !isViewingActiveClinic && clinicId) {
      setActiveClinic(clinicId);
    }
  }, [clinicUser?.clinic.id, isViewingActiveClinic, clinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isViewingActiveClinic) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await TeamApi.listTeamMembers();
        if (cancelled) return;
        const teamMembers = (response.body ?? []) as TeamMember[];
        setAdmins(
          teamMembers.filter(
            (m) =>
              m.role === ClinicUserRoles.Admin &&
              m.status === ClinicUserStatuses.Active,
          ),
        );
      } catch {
        if (!cancelled) setAdmins([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isViewingActiveClinic, clinicId]);

  if (!currentUser) {
    return <div data-testid="manage-clinic-detail-page-loading" />;
  }

  if (!clinicUser) {
    return (
      <div data-testid="manage-clinic-detail-page-not-found">
        <Tile>
          <Link to="/manage-clinic" className={styles.backLink}>
            <i className="fa-light fa-chevron-left" /> Back to Clinic Settings
          </Link>
          <div className="padding-top-md">
            <p>Clinic not found, or you no longer have access to it.</p>
          </div>
        </Tile>
      </div>
    );
  }

  const clinic = clinicUser.clinic;
  const address = formatAddressLine(clinic);
  const isClinicAdmin =
    clinicUser.role === ClinicUserRoles.Admin &&
    clinicUser.status === ClinicUserStatuses.Active;

  // Until the per-clinic data fetch is wired through the embedded widgets,
  // we rely on the global active-clinic context which gets switched in the
  // useEffect above.
  const widgetsReady = isViewingActiveClinic;

  return (
    <div className={styles.root} data-testid="manage-clinic-detail-page">
      <Tile>
        <div className={styles.header}>
          <Link
            to="/manage-clinic"
            className={styles.backLink}
            data-testid="manage-clinic-detail-page-back-link"
          >
            <i className="fa-light fa-chevron-left" /> Back to Clinic Settings
          </Link>
        </div>

        <div className={styles.clinicHeader}>
          <UserAvatar
            userInitials={clinicInitials(clinic)}
            size={UserAvatarSizes.Large}
            backgroundColor="#36cbb9"
          />
          <div className={styles.clinicHeaderText}>
            <div className={styles.clinicName}>{clinic.name}</div>
            {!!address && (
              <div className={styles.clinicAddress}>{address}</div>
            )}
          </div>
          {isClinicAdmin && (
            <Button
              className="btn btn-secondary btn-sm"
              onClick={() => setEditOpen(true)}
              data-testid="manage-clinic-detail-page-edit-info-button"
            >
              <i
                className="fa-light fa-pencil"
                style={{ marginRight: "0.375rem" }}
              />
              Edit Info
            </Button>
          )}
        </div>

        <div className={styles.body}>
          <div className={styles.mainColumn}>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Manage Suppliers</h2>
                <Button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate("/account/distributors")}
                  data-testid="manage-clinic-detail-connect-supplier-button"
                >
                  Connect Supplier
                </Button>
              </div>
              {widgetsReady ? (
                <ClinicSuppliersTable />
              ) : (
                <div className={styles.sectionPlaceholder}>
                  Loading supplier connections…
                </div>
              )}
            </section>

            <section className={styles.section}>
              {widgetsReady ? (
                <AccountHiddenItemSettings />
              ) : (
                <div className={styles.sectionPlaceholder}>
                  Loading hidden items…
                </div>
              )}
            </section>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.adminCard}>
              <div className={styles.adminCardLabel}>
                Clinic Admin{admins.length > 1 ? "s" : ""}
              </div>
              {admins.length === 0 ? (
                <div className={styles.adminContact}>
                  {widgetsReady
                    ? "No active admins found for this clinic."
                    : "Loading clinic admins…"}
                </div>
              ) : (
                admins.map((admin) => (
                  <div key={admin.id} className={styles.adminCardBody}>
                    <UserAvatar
                      userInitials={userInitials(admin.fullName)}
                      size={UserAvatarSizes.Medium}
                    />
                    <div className={styles.adminCardText}>
                      <div className={styles.adminName}>{admin.fullName}</div>
                      {!!admin.email && (
                        <div className={styles.adminContact}>{admin.email}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </div>
      </Tile>

      {isClinicAdmin && (
        <EditClinicInfoModal
          isShowing={editOpen}
          onClose={() => setEditOpen(false)}
          clinic={clinic}
        />
      )}
    </div>
  );
}
